import { IconButton, Switch, Tag } from '@ads/components-react';

import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type { ExamLangListItemViewModel } from '@/shared/api/generated/types/ExamLangListItemViewModel';
import { Spinner } from '@/shared/ui/atoms';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';
import { getCultureTag } from '@/shared/utils/cultureTag';

export interface ExamLanguageListProps {
  readonly languages: readonly ExamLangListItemViewModel[];
  readonly isLoading: boolean;
  readonly editingLangId?: number | null;
  readonly onEdit?: (lang: ExamLangListItemViewModel) => void;
  readonly onToggleStatus?: (id: number, cultureInfo: string, active: boolean) => void;
  readonly pendingToggleId?: number | null;
}

/**
 * Lista os idiomas configurados para a avaliação, permitindo editar e
 * alternar o status (ativo/rascunho) de cada idioma individualmente.
 *
 * @param languages - Idiomas da avaliação (ativos e rascunhos)
 * @param isLoading - Exibe spinner enquanto carrega
 * @param editingLangId - ID do idioma em edição (destaque visual)
 * @param onEdit - Callback ao clicar em editar
 * @param onToggleStatus - Callback ao alternar o status ativo/rascunho
 * @param pendingToggleId - ID do idioma com toggle em andamento
 */
export function ExamLanguageList({
  languages,
  isLoading,
  editingLangId,
  onEdit,
  onToggleStatus,
}: ExamLanguageListProps) {
  const { t } = useTranslation('examManagement');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-xl">
        <Spinner size="md" />
      </div>
    );
  }

  if (languages.length === 0) {
    return <EmptyState title={t('languages.minRequired')} />;
  }

  return (
    <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1">
      <div className="border-b-hairline border-ctx-layout-border px-sm py-mini">
        <h3 className="ts-subtitle-xs text-ctx-content-title">{t('languages.list.title')}</h3>
        <p className="ts-paragraph-xxs text-ctx-content-base mt-quark">
          {t('languages.list.subtitle', { count: languages.length })}
        </p>
      </div>

      <div className="flex flex-col">
        {languages.map((lang, idx) => (
          <div
            key={lang.id}
            className={`flex items-center gap-xs px-sm py-xs ${idx > 0 ? 'border-t-hairline border-ctx-layout-border' : ''} ${editingLangId === lang.id ? 'bg-base-accent-50' : ''}`}
          >
            <Tag {...getCultureTag(lang.cultureInfo ?? '')} size="sm" />

            <span className="flex-1 ts-paragraph-xs text-ctx-content-title">
              {lang.cultureInfoDisplay ?? lang.cultureInfo}
            </span>

            <Tag
              label={
                lang.active !== false
                  ? t('languages.list.statusActive')
                  : t('languages.list.statusDraft')
              }
              status={lang.active !== false ? 'positive' : 'helper'}
              size="md"
              custom={{ text: 'text-nowrap' }}
            />

            {onEdit && (
              <IconButton
                icon={PencilSquareIcon}
                ariaLabel={t('languages.list.edit')}
                hierarchy="tertiary"
                size="md"
                onClick={() => onEdit(lang)}
              />
            )}

            {onToggleStatus && (
              <Switch
                checked={lang.active ?? false}
                size="sm"
                onCheckedChange={checked => {
                  onToggleStatus(lang.id ?? 0, lang.cultureInfo ?? '', checked === true);
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
